export enum TaskStatus {
  Backlog = 1,
  Todo = 2,
  InProgress = 3,
  InReview = 4,
  Done = 5,
  Cancelled = 6,
}

export enum TaskPriority {
  Low = 1,
  Medium = 2,
  High = 3,
  Urgent = 4,
}

export enum TaskType {
  Epic = 1,
  Task = 2,
  Subtask = 3,
}

export const TASK_TYPE_LABELS: Record<number, string> = {
  [TaskType.Epic]: "Épico",
  [TaskType.Task]: "Tarefa",
  [TaskType.Subtask]: "Subtarefa",
};

export const TASK_STATUS_LABELS: Record<number, string> = {
  [TaskStatus.Backlog]: "Backlog",
  [TaskStatus.Todo]: "A Fazer",
  [TaskStatus.InProgress]: "Em Andamento",
  [TaskStatus.InReview]: "Em Revisão",
  [TaskStatus.Done]: "Concluído",
  [TaskStatus.Cancelled]: "Cancelado",
};

export const TASK_PRIORITY_LABELS: Record<number, string> = {
  [TaskPriority.Low]: "Baixa",
  [TaskPriority.Medium]: "Média",
  [TaskPriority.High]: "Alta",
  [TaskPriority.Urgent]: "Urgente",
};

// Colunas padrão do board (kanban). Mantido apenas como fallback — o board agora carrega as
// colunas (incluindo customizadas como "Blocked") da API via getBoardColumns().
export const BOARD_COLUMNS: TaskStatus[] = [
  TaskStatus.Backlog,
  TaskStatus.Todo,
  TaskStatus.InProgress,
  TaskStatus.InReview,
  TaskStatus.Done,
];

/**
 * Categoria semântica de uma coluna do board. As regras (capacity, cronograma, conclusão, atraso)
 * decidem pela categoria, não pelo id da coluna — assim colunas customizadas funcionam corretamente.
 */
export type BoardColumnCategory =
  | "NotStarted"
  | "Active"
  | "OnHold"
  | "Done"
  | "Cancelled";

export const BOARD_CATEGORY_LABELS: Record<BoardColumnCategory, string> = {
  NotStarted: "Não iniciada",
  Active: "Em andamento",
  OnHold: "Parada (bloqueada)",
  Done: "Concluída",
  Cancelled: "Cancelada",
};

/** Coluna/etapa do board (global a todos os projetos). */
export interface BoardColumn {
  id: number;
  name: string;
  category: BoardColumnCategory;
  position: number;
  color?: string | null;
  showOnBoard: boolean;
  isSystem: boolean;
}

/** Cria/edita uma coluna do board. */
export interface BoardColumnRequest {
  name: string;
  category?: BoardColumnCategory;
  color?: string | null;
  showOnBoard: boolean;
}

export interface ProjectTask {
  id: number;
  projectId: number;
  projectName?: string;
  parentTaskId?: number | null;
  type?: number;
  typeName?: string;
  subtaskCount?: number;
  completedSubtaskCount?: number;
  title: string;
  description?: string;
  estimatedHours?: number;
  assigneeEmployeeId?: number;
  assigneeName?: string;
  assignees?: { id: number; name?: string }[];
  startDate?: string;
  dueDate?: string;
  /** Coluna do board (id). */
  status: number;
  statusName: string;
  /** Categoria semântica da coluna atual. */
  statusCategory: BoardColumnCategory;
  /** Cor da coluna atual (hex/token), para pintar badges/cards sem cruzar com a lista de colunas. */
  statusColor?: string | null;
  priority: number;
  priorityName: string;
  orderIndex: number;
  completedAt?: string;
  dtCreated: string;
  scheduleLocked: boolean;
  isOverdue: boolean;
  scheduleState: "done" | "cancelled" | "overdue" | "due_soon" | "on_track" | "no_due_date";
  dependsOnTaskIds: number[];
}

export interface ProjectTaskRequest {
  projectId: number;
  parentTaskId?: number | null;
  type?: number;
  title: string;
  description?: string;
  estimatedHours?: number | null;
  assigneeEmployeeId?: number | null;
  assigneeEmployeeIds?: number[];
  status?: number;
  priority?: number;
  dependsOnTaskIds?: number[];
  scheduleLocked?: boolean;
}

export interface UpdateProjectTaskRequest extends ProjectTaskRequest {
  id: number;
}

export interface AutoScheduleResult {
  scheduled: number;
  skippedLocked: number;
  skippedNoAssigneeOrEstimate: number;
}

/** Uma linha do cronograma: horas planejadas para a tarefa num dia. */
export interface TaskScheduleLine {
  date: string;
  plannedHours: number;
}

/** Cronograma completo de uma tarefa (com prazo derivado). */
export interface TaskScheduleResponse {
  taskId: number;
  startDate?: string;
  dueDate?: string;
  totalPlannedHours: number;
  lines: TaskScheduleLine[];
}

/** Tarefa do portal disponível para lançamento de horas (com cronograma planejado). */
export interface TimesheetTaskOption {
  taskId: number;
  projectId: number;
  title: string;
  estimatedHours?: number;
  status: number;
  statusName: string;
  schedule: TaskScheduleLine[];
}
